import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { v4 as uuidgen } from 'uuid';
import { RadioButton } from '../../../components/RadioButton';
import { TextInput } from '../../../components/TextInput';
import { baseStyleSheet } from '../../../styles/styles';
import { styleSheet } from './styles';

export enum SendOptionCompleteByOption {
	ASAP = 'complete-by-asap-option',
	SPREAD = 'complete-by-spread-option',
}

/**
 * Spreading a campaign over MORE days is always achievable, so the ceiling only has to stop typos.
 * A cap that can land below the floor is what locked this input for large campaigns.
 */
const maxSpreadDays = 365;

const overlapDisclaimerText =
	'Other campaigns scheduled in the same window may extend the completion date beyond the number of days you select.';

interface IProps {
	className?: string;
	/** Estimated calendar days from the backend, accounting for weekends/holidays. */
	estimatedCalendarDays?: number;
	maxEmailsPerDay: number;
	onOptionChange(option: SendOptionCompleteByOption): void;
	onSendOptionCompleteByChange(numberOfDays?: number): void;
	recipients: number;
	styles?: StyleDeclarationValue[];
}

export const EditSendOptionsCompleteBy: React.FC<IProps> = props => {
	const {
		className,
		estimatedCalendarDays,
		maxEmailsPerDay,
		onOptionChange,
		onSendOptionCompleteByChange,
		recipients,
		styles,
	} = props;
	const radioGroupId = React.useRef(`complete-by-option-group-${uuidgen()}`).current;
	const radioIds = React.useRef<{ asap: string; spread: string }>({
		asap: SendOptionCompleteByOption.ASAP,
		spread: SendOptionCompleteByOption.SPREAD,
	}).current;
	const sendRateFloor = Math.ceil(recipients / maxEmailsPerDay);
	/**
	 * The floor is this campaign's own send rate, NOT estimatedCalendarDays: the estimate also covers
	 * already-queued campaigns, and the user is free to spread around those (see the disclaimer below).
	 */
	const minDays = Number.isFinite(sendRateFloor) ? Math.max(1, sendRateFloor) : 1;
	const maxNumDays = Math.max(maxSpreadDays, minDays);
	const estimateDays = Number.isFinite(estimatedCalendarDays) ? estimatedCalendarDays : minDays;
	const defaultDays = Math.min(maxNumDays, Math.max(minDays, estimateDays));
	const [completeByDays, setCompleteByDays] = React.useState(defaultDays);
	const [daysDraft, setDaysDraft] = React.useState(() => `${defaultDays}`);
	const [selectedId, setSelectedId] = React.useState(radioIds.asap);
	const hasUserEdited = React.useRef(false);
	const hasEditSinceBlur = React.useRef(false);

	// Sync when the backend estimate arrives (only before the user has manually edited)
	useEffect(() => {
		if (hasUserEdited.current) return;
		setCompleteByDays(defaultDays);
		setDaysDraft(`${defaultDays}`);
	}, [defaultDays]);

	useEffect(() => {
		const clamped = Math.min(maxNumDays, Math.max(minDays, completeByDays));
		if (clamped !== completeByDays) {
			setCompleteByDays(clamped);
			setDaysDraft(`${clamped}`);
		}
	}, [completeByDays, maxNumDays, minDays]);

	useEffect(() => {
		onSendOptionCompleteByChange(selectedId === SendOptionCompleteByOption.ASAP ? null : completeByDays);
	}, [completeByDays, onSendOptionCompleteByChange, selectedId, maxEmailsPerDay, recipients]);

	const selectSpreadOption = React.useCallback(() => {
		if (selectedId === radioIds.spread) {
			return;
		}
		setSelectedId(radioIds.spread);
		onOptionChange(SendOptionCompleteByOption.SPREAD);
	}, [onOptionChange, radioIds.spread, selectedId]);

	const onCompleteByDaysChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			hasUserEdited.current = true;
			hasEditSinceBlur.current = true;
			const nextDraft = e.target.value;
			setDaysDraft(nextDraft);

			const num = parseInt(nextDraft, 10);
			if (Number.isNaN(num) || num < minDays || num > maxNumDays) {
				return;
			}
			selectSpreadOption();
			setCompleteByDays(num);
		},
		[maxNumDays, minDays, selectSpreadOption]
	);
	const onCompleteByDaysBlurred = React.useCallback(() => {
		if (!hasEditSinceBlur.current) {
			return;
		}
		hasEditSinceBlur.current = false;
		const num = parseInt(daysDraft, 10);
		const nextDays = Number.isNaN(num) ? completeByDays : Math.min(maxNumDays, Math.max(minDays, num));
		selectSpreadOption();
		setCompleteByDays(nextDays);
		setDaysDraft(`${nextDays}`);
	}, [completeByDays, daysDraft, maxNumDays, minDays, selectSpreadOption]);
	const onCompleteByOptionChanged = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				setSelectedId(e.target.id);
				setCompleteByDays(defaultDays);
				setDaysDraft(`${defaultDays}`);
				onOptionChange(e.target.id as SendOptionCompleteByOption);
			}
		},
		[onOptionChange, defaultDays]
	);

	const getPerDay = useCallback(() => {
		return Math.ceil(recipients / completeByDays);
	}, [recipients, completeByDays]);

	return (
		<div className={`${css(styleSheet.EditSendOptionsCompleteBy, ...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.radioOptions)}>
				<RadioButton
					checked={selectedId === radioIds.asap}
					className={css(styleSheet.radio)}
					iconClassName={css(styleSheet.radioIcon)}
					id={radioIds.asap}
					labelClassName={css(styleSheet.radioLabel, styleSheet.radioLabelLeft)}
					name={radioGroupId}
					onChange={onCompleteByOptionChanged}
				>
					Send as quickly as possible
					{typeof maxEmailsPerDay === 'number' && (
						<div className={css(styleSheet.perDayLabel)}>{`${maxEmailsPerDay} email${
							maxEmailsPerDay > 1 ? 's' : ''
						}/day`}</div>
					)}
				</RadioButton>
				<RadioButton
					checked={selectedId === radioIds.spread}
					className={css(styleSheet.radio)}
					iconClassName={css(styleSheet.radioIcon)}
					id={radioIds.spread}
					labelClassName={css(styleSheet.radioLabel)}
					name={radioGroupId}
					onChange={onCompleteByOptionChanged}
				>
					<div className={css(baseStyleSheet.verticalStack)}>
						<div className={css(baseStyleSheet.horizontalStack)}>
							<span>Spread out over</span>
							<TextInput
								className={css(baseStyleSheet.textField)}
								inputClassName={css(styleSheet.completeByDays)}
								inputId='complete-by-days-input'
								max={maxNumDays}
								min={minDays}
								onBlur={onCompleteByDaysBlurred}
								onChange={onCompleteByDaysChanged}
								step={1}
								type='number'
								value={daysDraft}
							/>
							<span className={css(styleSheet.fixedWidth30)}>{`day${completeByDays > 1 ? 's' : ''}`}</span>
						</div>
					</div>
					{!!maxEmailsPerDay && !!recipients && (
						<div className={css(styleSheet.perDayLabel)}>{`${getPerDay()} email${getPerDay() > 1 ? 's' : ''}/day`}</div>
					)}
				</RadioButton>
			</div>
			<div className={css(styleSheet.overlapDisclaimer)}>{overlapDisclaimerText}</div>
		</div>
	);
};
